import { SharePointConsentRequiredError } from "components/sharepoint-browser/definitions";
import { getConsentRequiredError } from "components/sharepoint-browser/utils";
import { INotification } from "features/notifications/definitions";
import { Document } from "models/schemas";
import {
  DocumentIngestion,
  IngestDocumentsResponse,
} from "pages/gaia-ai/definitions";
import { GaiaConstants } from "utils/gaia-constants";
import { getFileNameWithExtension, isSharePointPointer } from "./utils";

/**
 * Most SharePoint files one ingest request may copy in: the service copies
 * them while the request is open and refuses the whole request above this.
 */
export const SHAREPOINT_KB_BATCH_SIZE = 5;

/**
 * A SharePoint file whose content is not in Gaia yet. Knowledge base approval
 * copies these on approve, at most `SHAREPOINT_KB_BATCH_SIZE` per request.
 */
export const needsSharePointCopy = (doc: Document): boolean =>
  isSharePointPointer(doc) && !doc.s3Key;

export type SendIngestBatch = (
  items: DocumentIngestion[],
  ackTeamWideVisibility: boolean,
) => Promise<IngestDocumentsResponse>;

export interface SharePointKbIngestOutcome {
  succeeded: DocumentIngestion[];
  failed: DocumentIngestion[];
  /** Set when the user must sign in to SharePoint; later batches are not sent. */
  consent?: SharePointConsentRequiredError;
}

export const sharePointCopyingHeader = (count: number) =>
  count === 1
    ? "Copying 1 file from SharePoint..."
    : `Copying ${count} files from SharePoint...`;

const filesLabel = (count: number) =>
  count === 1 ? "1 file" : `${count} files`;

const FAILED_REASON_FALLBACK = "We couldn't add this file. Try again.";

const SHAREPOINT_NOT_SENT_REASON =
  "Not sent. Sign in to SharePoint, then add this file again.";

const NOT_SENT_REASON = "Not sent. Try again.";

/** One selected file, as the batching and its notification need it. */
export interface KbIngestFile {
  ingestion: DocumentIngestion;
  name: string;
  isSharePoint: boolean;
}

export const toKbIngestFile = (doc: Document): KbIngestFile => ({
  ingestion: { documentId: doc.documentId },
  name: getFileNameWithExtension(doc),
  isSharePoint: isSharePointPointer(doc),
});

/**
 * Ingests a selection that contains SharePoint files: the SharePoint files go
 * first, at most `SHAREPOINT_KB_BATCH_SIZE` per request and acknowledged, then
 * every other file in one request without the acknowledgement, as without
 * SharePoint. Sending SharePoint files first means a sign-in prompt stops the
 * run before anything was added, so the user can simply try again.
 */
export const ingestInSharePointBatches = async (
  files: KbIngestFile[],
  send: SendIngestBatch,
): Promise<SharePointKbIngestOutcome> => {
  const pointers = files.filter((f) => f.isSharePoint).map((f) => f.ingestion);
  const others = files.filter((f) => !f.isSharePoint).map((f) => f.ingestion);
  const batches: { items: DocumentIngestion[]; ack: boolean }[] = [];
  for (let i = 0; i < pointers.length; i += SHAREPOINT_KB_BATCH_SIZE) {
    batches.push({
      items: pointers.slice(i, i + SHAREPOINT_KB_BATCH_SIZE),
      ack: true,
    });
  }
  if (others.length) {
    batches.push({ items: others, ack: false });
  }

  const outcome: SharePointKbIngestOutcome = { succeeded: [], failed: [] };
  for (const batch of batches) {
    try {
      const result = await send(batch.items, batch.ack);
      outcome.succeeded.push(...(result.succeededDocuments ?? []));
      outcome.failed.push(...(result.failedDocuments ?? []));
    } catch (error) {
      const consent = getConsentRequiredError(error);
      if (consent) {
        outcome.consent = consent;
        return outcome;
      }
      outcome.failed.push(
        ...batch.items.map(({ documentId }) => ({ documentId })),
      );
    }
  }
  return outcome;
};

/** An auto-approved submit lists only the files that were not added. */
export const toAutoApproveOutcome = (
  files: KbIngestFile[],
  failed: DocumentIngestion[] = [],
): SharePointKbIngestOutcome => {
  const failedIds = new Set(failed.map((f) => f.documentId));
  return {
    succeeded: files
      .filter((f) => !failedIds.has(f.ingestion.documentId))
      .map((f) => f.ingestion),
    failed,
  };
};

const getFileName = (documentId: string | undefined, files: KbIngestFile[]) =>
  files.find((f) => f.ingestion.documentId === documentId)?.name ??
  documentId ??
  "";

/**
 * Flash message for a knowledge base request that included SharePoint files:
 * success when every file was queued, otherwise a warning (some queued) or an
 * error (none) listing each file that was not added and why. Returns
 * undefined when nothing was sent, as when the user must sign in first. When
 * a sign-in prompt stopped the run part way, the files never sent are listed
 * as not added.
 */
export const buildSharePointKbIngestNotification = (
  outcome: SharePointKbIngestOutcome,
  files: KbIngestFile[],
): INotification | undefined => {
  const succeeded = outcome.succeeded.length;
  if (!succeeded && !outcome.failed.length) {
    return undefined;
  }
  const failed = [...outcome.failed];
  if (outcome.consent) {
    const answered = new Set(
      [...outcome.succeeded, ...outcome.failed].map((d) => d.documentId),
    );
    failed.push(
      ...files
        .filter((f) => !answered.has(f.ingestion.documentId))
        .map((f) => ({
          documentId: f.ingestion.documentId,
          ingestionStatus: f.isSharePoint
            ? SHAREPOINT_NOT_SENT_REASON
            : NOT_SENT_REASON,
        })),
    );
  }
  if (!failed.length) {
    return {
      type: "success",
      header: `${filesLabel(succeeded)} submitted for ingestion into the knowledge base.`,
      content:
        "SharePoint files are ingested after their malware scan. The KB index status column on the Files tab shows progress.",
      dismissible: true,
      timeout: GaiaConstants.NOTIFICATION_TIMEOUT.LONG,
    };
  }
  return {
    type: succeeded ? "warning" : "error",
    header: succeeded
      ? `${succeeded} of ${succeeded + failed.length} files submitted for ingestion into the knowledge base.`
      : failed.length === 1
        ? "1 file wasn't added to the knowledge base."
        : `${failed.length} files weren't added to the knowledge base.`,
    content: (
      <>
        Not added:
        <ul>
          {failed.map((failure, index) => (
            <li key={failure.documentId ?? index}>
              {getFileName(failure.documentId, files)}:{" "}
              {failure.ingestionStatus || FAILED_REASON_FALLBACK}
            </li>
          ))}
        </ul>
      </>
    ),
    dismissible: true,
  };
};
